/**
 * GameDB — loads and validates the canonical data mirror.
 *
 * Source of truth is `data/*.json` in LegionnaireRTS/Legionnaire. This repo
 * holds a read-only synced copy (see tools/sync_data.py, gitignored under
 * public/data/). Design changes are made in the main repo, never here.
 */
import type {
  BuildingDef,
  EchelonDef,
  FactionDef,
  FormationDef,
  ResourceDef,
  TechDef,
  TechEffect,
  UnitDef,
} from "./types";
import { ArtMap, unmappedModel, type ResolvedModel } from "./artmap";
import { PROTOTYPE_TECHS } from "./prototype_overlay";

/** data/*.json files mirrored from the main repo. Missing files warn, not fail. */
const DATA_FILES = [
  "factions",
  "resources",
  "formations",
  "echelons",
  "technologies",
  "equipment",
  "units_rome",
  "units_samnite",
  "units_gauls",
  "buildings_rome",
  "buildings_samnite",
  "buildings_gauls",
  "localization_en",
  "art_manifest",
] as const;

function pickArray(doc: unknown, keys: string[]): unknown[] {
  if (Array.isArray(doc)) return doc;
  if (doc && typeof doc === "object") {
    for (const k of keys) {
      const v = (doc as Record<string, unknown>)[k];
      if (Array.isArray(v)) return v;
    }
  }
  return [];
}

export class GameDB {
  factions = new Map<string, FactionDef>();
  resources = new Map<string, ResourceDef>();
  formations = new Map<string, FormationDef>();
  echelons = new Map<string, EchelonDef>();
  technologies = new Map<string, TechDef>();
  units = new Map<string, UnitDef>();
  buildings = new Map<string, BuildingDef>();
  strings = new Map<string, string>();
  /** Canonical asset/ID mapping (art_manifest.json + vendored web assets). */
  art: ArtMap = new ArtMap();
  warnings: string[] = [];

  private warn(msg: string) {
    this.warnings.push(msg);
    console.warn(`[gamedb] ${msg}`);
  }

  static async load(basePath = `${import.meta.env.BASE_URL}data`): Promise<GameDB> {
    const db = new GameDB();
    const docs = new Map<string, unknown>();
    await Promise.all(
      DATA_FILES.map(async (name) => {
        try {
          const res = await fetch(`${basePath}/${name}.json`);
          if (!res.ok) {
            db.warn(`missing data file: ${name}.json (HTTP ${res.status})`);
            return;
          }
          docs.set(name, await res.json());
        } catch (e) {
          db.warn(`failed to load ${name}.json: ${String(e)}`);
        }
      }),
    );

    const asFaction = (r: unknown): FactionDef =>
      r as FactionDef;
    for (const r of pickArray(docs.get("factions"), ["factions"])) {
      const f = asFaction(r);
      if (!f.id || !f.palette) {
        db.warn(`skipping malformed faction entry`);
        continue;
      }
      db.factions.set(f.id, f);
    }
    for (const r of pickArray(docs.get("resources"), ["resources"])) {
      const d = r as ResourceDef;
      if (d.id) db.resources.set(d.id, d);
    }
    for (const r of pickArray(docs.get("formations"), ["formations"])) {
      const d = r as FormationDef;
      if (!d.id || typeof d.spacing !== "number") {
        db.warn(`skipping malformed formation entry`);
        continue;
      }
      db.formations.set(d.id, d);
    }
    for (const r of pickArray(docs.get("echelons"), ["echelons"])) {
      const d = r as EchelonDef;
      if (d.id) db.echelons.set(d.id, d);
    }
    for (const r of pickArray(docs.get("technologies"), ["technologies"])) {
      const d = r as Record<string, unknown>;
      if (typeof d["id"] !== "string") continue;
      const requires = (d["requires"] as Record<string, string[]>) ?? {};
      db.technologies.set(d["id"] as string, {
        id: d["id"] as string,
        name_en: (d["name_en"] as string) ?? (d["id"] as string),
        echelon: (d["echelon"] as number) ?? 1,
        cost: (d["cost"] as Record<string, number>) ?? {},
        research_time: (d["research_time"] as number) ?? 30,
        requires: {
          techs: requires["techs"] ?? [],
          buildings: requires["buildings"] ?? [],
        },
        researched_at: (d["researched_at"] as string[]) ?? [],
        effects: (d["effects"] as TechEffect[]) ?? [],
        extra: d,
      });
    }
    // Prototype overlay: gameplay the prototype needs before UE data
    // adopts it (e.g. the Dilectus command-scaling line). Injected AFTER
    // the mirror load; a canonical id collision never overwrites data.
    for (const t of PROTOTYPE_TECHS) {
      if (db.technologies.has(t.id)) {
        db.warn(`prototype tech ${t.id} collides with canonical data — skipped`);
        continue;
      }
      db.technologies.set(t.id, t);
    }

    for (const file of ["units_rome", "units_samnite", "units_gauls"] as const) {
      const doc = docs.get(file);
      const keys = ["units", file];
      for (const r of pickArray(doc, keys)) {
        const d = r as Record<string, unknown>;
        if (typeof d["id"] !== "string") continue;
        db.units.set(d["id"] as string, {
          id: d["id"] as string,
          name_en: (d["name_en"] as string) ?? (d["id"] as string),
          faction: (d["faction"] as string) ?? file.replace("units_", ""),
          echelon: (d["echelon"] as number) ?? 1,
          extra: d,
        });
      }
    }
    for (const file of ["buildings_rome", "buildings_samnite", "buildings_gauls"] as const) {
      const doc = docs.get(file);
      for (const r of pickArray(doc, ["buildings", file])) {
        const d = r as Record<string, unknown>;
        if (typeof d["id"] !== "string") continue;
        db.buildings.set(d["id"] as string, {
          id: d["id"] as string,
          name_en: (d["name_en"] as string) ?? (d["id"] as string),
          faction: (d["faction"] as string) ?? file.replace("buildings_", ""),
          extra: d,
        });
      }
    }
    const loc = docs.get("localization_en") as Record<string, unknown> | undefined;
    const stringsObj =
      loc && typeof loc["strings"] === "object"
        ? (loc["strings"] as Record<string, unknown>)
        : loc;
    if (stringsObj) {
      for (const [k, v] of Object.entries(stringsObj)) {
        if (typeof v === "string") db.strings.set(k, v);
      }
    }

    // Canonical asset/ID mapping. The vendored web-asset list lives outside
    // the data mirror (public/assets/manifest.json); absent -> no GLBs yet,
    // everything resolves to placeholders. Never a fatal error.
    let vendoredDoc: unknown = null;
    try {
      const res = await fetch(`${import.meta.env.BASE_URL}assets/manifest.json`);
      if (res.ok) vendoredDoc = await res.json();
    } catch {
      /* no vendored web assets */
    }
    db.art = ArtMap.fromDocs(docs.get("art_manifest"), vendoredDoc);

    db.validate();
    return db;
  }

  /** Cross-file consistency checks. Mirrors the spirit of tools/validate_project.py. */
  validate(): void {
    for (const [id, f] of this.factions) {
      // Unimplemented factions legitimately omit starting forces.
      for (const uid of Object.keys(f.starting_units ?? {})) {
        if (!this.units.has(uid)) this.warn(`faction ${id} starts with unknown unit ${uid}`);
      }
      for (const bid of Object.keys(f.starting_buildings ?? {})) {
        if (!this.buildings.has(bid)) this.warn(`faction ${id} starts with unknown building ${bid}`);
      }
      for (const rid of Object.keys(f.starting_resources ?? {})) {
        if (!this.resources.has(rid)) this.warn(`faction ${id} starts with unknown resource ${rid}`);
      }
    }
    for (const [id, f] of this.formations) {
      if (f.allowed_factions) {
        for (const fid of f.allowed_factions) {
          if (!this.factions.has(fid)) this.warn(`formation ${id} allows unknown faction ${fid}`);
        }
      }
    }
  }

  playableFactions(): FactionDef[] {
    return [...this.factions.values()].filter((f) => f.playable && f.implemented);
  }

  /**
   * Resolve a building id to its web model at a faction echelon.
   * Render code uses this (never hardcoded paths). Unmapped ids resolve to
   * a placeholder descriptor (`canonicalId: null`).
   */
  buildingModel(id: string, echelon: number): ResolvedModel {
    const def = this.buildings.get(id);
    return def ? this.art.buildingModel(def, echelon) : unmappedModel();
  }

  /** Resolve a unit id to its web model (placeholder descriptor when unmapped). */
  unitModel(id: string): ResolvedModel {
    const def = this.units.get(id);
    return def ? this.art.unitModel(def) : unmappedModel();
  }

  t(key: string, fallback: string): string {
    return this.strings.get(key) ?? fallback;
  }
}
